/* eslint-disable react/no-unstable-nested-components, react-hooks/exhaustive-deps, no-console */
import { lazy, memo, startTransition, useCallback } from "react"
import type { ComponentProps, FC, NamedExoticComponent, ReactElement, ReactNode } from "react"

import { useBoolean } from "react-use"

import { ESpinnerSize, ErrorHint, Loadable, Spinner } from "@/3514/components"
import { useCSSInsertion } from "@/3514/hooks"
import { buildCssClassName, propsAreEqual } from "@/3514/utils"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import { EVideoRendererSourceProvider as EProvider } from "./video-renderer.types"
import { videoRendererUtils as utils } from "./video-renderer.utils"

const { getVideoLinkByProvider } = utils

const LazyPlayer = lazy((): Promise<typeof import("react-player/lazy")> => import("react-player/lazy"))

type TProps = Omit<ComponentProps<typeof LazyPlayer>, "url" | "playsinline"> & {
    url: string
    playsInline?: boolean
    errorText?: string
    provider?: EProvider
    renderContainer(children: ReactNode): ReactElement
}

const classes = {
    container: (isPlayerLoading: boolean, isPlayerCrashed: boolean): string =>
        buildCssClassName("relative w-full h-full", isPlayerLoading && "min-h-[100px]", isPlayerCrashed && "hidden"),
    playerWrapper: (isPlayerLoading: boolean): string =>
        buildCssClassName("video-renderer w-full h-full", isPlayerLoading && "hidden"),
    playerInsertableClasses: (): string => `
        .video-renderer iframe { 
          @media screen and (max-width: 1199px) {
            aspect-ratio: 16 / 9;
            height: unset !important;
          }
        }
    `,
    spinnerWrapper: (): string => "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
}

const Component: FC<TProps> = ({
    controls = false,
    playsInline = false,
    width = "100%",
    height = "100%",
    url,
    onReady = emptyCallback,
    onError = emptyCallback,
    provider = EProvider.Vimeo,
    errorText = undefined,
    renderContainer,
    ...rest
}: TProps): ReactElement => {
    useCSSInsertion({
        cssString: classes.playerInsertableClasses()
    })

    const [isPlayerLoading, setIsPlayerLoading]: ReturnType<typeof useBoolean> = useBoolean(true)

    const [isPlayerCrashed, setIsPlayerCrashed]: ReturnType<typeof useBoolean> = useBoolean(false)

    function PlayerWrapper({ children }: { children: ReactNode }): ReactElement {
        return <div className={classes.playerWrapper(isPlayerLoading)}>{children}</div>
    }

    const handleReady: TEmptyCallback = useCallback((): void => {
        startTransition((): void => setIsPlayerLoading(false))

        onReady?.()
    }, [onReady])

    const handleError: (e: unknown) => void = useCallback(
        (e: unknown): void => {
            console.log(e)

            startTransition((): void => setIsPlayerCrashed(true))

            onError?.()
        },
        [onError]
    )

    return renderContainer(
        isPlayerCrashed ? (
            <ErrorHint isVisible {...(errorText && { text: errorText })} />
        ) : (
            <div className={classes.container(isPlayerLoading, isPlayerCrashed)}>
                {isPlayerLoading && <Spinner size={ESpinnerSize.Large} wrapperClassName={classes.spinnerWrapper()} />}

                <Loadable
                    component={
                        <LazyPlayer
                            url={getVideoLinkByProvider(url, provider)}
                            controls={controls}
                            playsinline={playsInline}
                            width={width}
                            height={height}
                            wrapper={PlayerWrapper}
                            onReady={handleReady}
                            onError={handleError}
                            {...rest}
                        />
                    }
                />
            </div>
        )
    )
}

Component.displayName = "VideoRenderer"

const MemoizedComponent: NamedExoticComponent<TProps> = memo<TProps>(Component, propsAreEqual)

export { MemoizedComponent as VideoRenderer }
